import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Cookie, Settings, Check, X } from 'lucide-react';

export type CookieConsent = {
  essential: boolean;
  analytics: boolean;
  timestamp: string;
};

// Function to open cookie settings from anywhere
export const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent('open-cookie-settings'));
};

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    }

    // Listen for custom event to open settings
    const handleOpenSettings = () => {
      const existingConsent = localStorage.getItem('cookie-consent');
      if (existingConsent) {
        try {
          const parsed = JSON.parse(existingConsent);
          setAnalyticsEnabled(parsed.analytics ?? true);
        } catch {
          setAnalyticsEnabled(true);
        }
      }
      setShowBanner(true);
      setShowSettings(true);
    };

    window.addEventListener('open-cookie-settings', handleOpenSettings);
    return () => window.removeEventListener('open-cookie-settings', handleOpenSettings);
  }, []);

  const saveConsent = (analytics: boolean) => {
    const consent: CookieConsent = {
      essential: true, // Always enabled
      analytics,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    setShowBanner(false);
    
    // Reload page to apply analytics settings
    if (analytics) {
      window.location.reload();
    }
  };

  const handleAcceptAll = () => {
    saveConsent(true);
  };

  const handleDeclineAll = () => {
    saveConsent(false);
  };

  const handleSaveSettings = () => {
    saveConsent(analyticsEnabled);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 animate-in slide-in-from-bottom duration-500 contain-layout">
      <Card className="max-w-4xl mx-auto bg-background/95 backdrop-blur-sm border shadow-2xl contain-layout">
        {!showSettings ? (
          // Main Banner
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2 text-base sm:text-lg">
                  Cookie-Einstellungen
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Wir verwenden Cookies und ähnliche Technologien, um die Website-Nutzung zu analysieren 
                  und unsere Inhalte zu verbessern. Ihre Daten werden anonymisiert gespeichert und nicht 
                  an Dritte weitergegeben.
                </p>
                <div className="space-y-2 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Essentiell:</strong> Notwendig für grundlegende Funktionen (immer aktiv)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Cookie className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span><strong>Analytics:</strong> Hilft uns, die Nutzung zu verstehen (Seitenaufrufe, Gerätetype, Verweildauer)</span>
                  </div>
                </div>
                <Link 
                  to="/privacy" 
                  className="text-xs text-primary hover:underline inline-block mt-3"
                >
                  Mehr in unserer Datenschutzerklärung →
                </Link>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                <Settings className="w-4 h-4 mr-2" />
                Einstellungen
              </Button>
              <div className="flex gap-2 flex-1 sm:flex-initial">
                <Button
                  onClick={handleDeclineAll}
                  variant="outline"
                  size="sm"
                  className="flex-1 sm:flex-initial"
                >
                  <X className="w-4 h-4 mr-2" />
                  Nur Essentiell
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  size="sm"
                  className="flex-1 sm:flex-initial bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // Settings View
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <Settings className="w-6 h-6 text-primary" />
              <h3 className="font-semibold text-foreground text-base sm:text-lg">
                Cookie-Einstellungen anpassen
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              {/* Essential Cookies */}
              <div className="flex items-start justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex-1 pr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm">Essentiell</h4>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                      Erforderlich
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Diese Cookies sind für die Grundfunktionen der Website notwendig 
                    und können nicht deaktiviert werden.
                  </p>
                </div>
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-primary" />
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex-1 pr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm">Analytics & Statistiken</h4>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Hilft uns zu verstehen, wie Besucher mit unserer Website interagieren. 
                    Wir erfassen: Seitenaufrufe, Gerätetype, Browser, Betriebssystem und Verweildauer.
                  </p>
                  <p className="text-xs text-muted-foreground italic">
                    Alle Daten werden anonymisiert und IP-Adressen werden gekürzt gespeichert.
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={analyticsEnabled}
                    onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                    className="w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                  />
                </label>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                Zurück
              </Button>
              <Button
                onClick={handleSaveSettings}
                size="sm"
                className="flex-1 sm:flex-initial bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Check className="w-4 h-4 mr-2" />
                Auswahl speichern
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CookieBanner;